/** Every check a filing is subject to, in every state (ADR-0163, `GET .../checks`).
 *
 * **A read that cannot default.** The hook throws on failure (`unwrap`), so a dropped request is a
 * failed read the screen draws as one and never "no checks", which on this screen would read as a
 * filing with nothing to pass. Nothing is stored server-side and nothing is written: running the
 * checks again is reading again, and the new `evaluated_at` is the proof that it happened.
 *
 * **One percentage, and it is the server's** (ADR-0212 decision 6): `summary.readiness_percent`, the
 * counted checks that passed over all counted checks, floored, `null` when nothing is counted. This
 * module never divides; the screen prints the field or a dash.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { unwrap } from "./queries";
import type { Scope2Method } from "./queries";
import type { components } from "./schema";

export type FilingChecks = components["schemas"]["FilingChecksOut"];
export type FilingCheck = components["schemas"]["FilingCheckOut"];
export type FilingCheckPart = components["schemas"]["FilingChecksPartOut"];
export type FilingCheckFix = components["schemas"]["FilingCheckFixOut"];

export const filingChecksKey = (obligationId: string, method: Scope2Method) =>
  ["filing-checks", obligationId, method] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** A response is checked, not trusted: a proxy answering `{}` is a failed read. */
function readChecks(value: unknown): FilingChecks {
  if (
    !isRecord(value) ||
    !Array.isArray(value.checks) ||
    !Array.isArray(value.parts) ||
    !isRecord(value.summary)
  ) {
    throw new Error("This filing's checks came back in a shape this screen cannot read.");
  }
  return value as unknown as FilingChecks;
}

/** The query for one filing's checks, shared by the hook and by a list that reads several
 * (`useQueries`) so both use one key and one function. */
export function filingChecksQuery(obligationId: string | undefined, method: Scope2Method) {
  return {
    queryKey: filingChecksKey(obligationId ?? "", method),
    enabled: Boolean(obligationId),
    // Always asked again on mount: a stale pass beside a fresh bill is the failure this product
    // exists to refuse.
    staleTime: 0,
    queryFn: async (): Promise<FilingChecks> =>
      readChecks(
        unwrap(
          await api.GET("/api/v1/reports/obligations/{obligation_id}/checks", {
            params: {
              path: { obligation_id: obligationId as string },
              query: { scope2_method: method },
            },
          }),
        ),
      ),
  };
}

export function useFilingChecks(obligationId: string | undefined, method: Scope2Method) {
  return useQuery(filingChecksQuery(obligationId, method));
}
